import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, Moon, Sun, Menu, X, ChevronDown, Fuel, Calculator, MapPin, ChevronRight } from 'lucide-react';
import { cityDropdownLinks } from '../data/fuelPricePages';

/* ─── FAQ data ─────────────────────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    q: 'Jak przeliczyć zużycie paliwa z MPG na kilometry na litr?',
    a: 'Przeliczenie zużycia paliwa z mil na galon (MPG) na kilometry na litr jest prostsze niż myślisz. Wystarczy pomnożyć wartość MPG przez współczynnik 0,425. Na przykład, jeśli samochód w USA ma spalanie 25 MPG, to po przeliczeniu otrzymujemy: 25 × 0,425 = 10,625 kilometrów na litr. Ten prosty wzór pozwoli Ci szybko oszacować, jak ekonomiczny jest amerykański samochód według standardów, do których jesteś przyzwyczajony. Warto pamiętać, że im wyższa wartość MPG, tym bardziej oszczędny jest pojazd - podobnie jak w przypadku kilometrów na litr. Nasz kalkulator wykonuje to obliczenie automatycznie, dzięki czemu możesz błyskawicznie porównać efektywność różnych modeli samochodów dostępnych w amerykańskich wypożyczalniach.',
  },
  {
    q: 'Jak przeliczyć MPG na litry na 100 kilometrów?',
    a: 'Przeliczenie spalania z mil na galon (MPG) na litry na 100 km, które jest standardem w Polsce i Europie, wymaga prostego działania matematycznego. Należy podzielić liczbę 235 przez wartość MPG. Wzór wygląda następująco: litry/100 km = 235 ÷ MPG. Przykładowo, jeśli samochód ma spalanie 30 MPG, to po przeliczeniu otrzymamy: 235 ÷ 30 = 7,83 litra na 100 km. Zauważ, że w przeciwieństwie do MPG, w przypadku litrów na 100 km - niższa wartość oznacza bardziej ekonomiczny samochód. Dlatego samochód o spalaniu 35 MPG (6,7 l/100 km) jest bardziej oszczędny niż pojazd o spalaniu 25 MPG (9,4 l/100 km). Nasz kalkulator automatycznie dokonuje tego przeliczenia, co pomaga w realnej ocenie ekonomiczności wynajmowanego w USA samochodu według znanych Ci standardów.',
  },
  {
    q: 'Jakie są najczęstsze błędy przy przeliczaniu galonów na litry?',
    a: 'Najczęstszym błędem przy przeliczaniu galonów na litry jest użycie niewłaściwego współczynnika konwersji. Wielu turystów błędnie zakłada, że galon to "około 4 litry", co prowadzi do niedoszacowania ilości paliwa o ponad 5%. Kolejny częsty błąd to mylenie galona amerykańskiego z galonem imperialnym - różnica między nimi wynosi prawie 20%! Niektórzy podróżnicy popełniają też błąd stosując odwrotny przelicznik (dzieląc litry przez 3,78 zamiast mnożyć), co całkowicie zniekształca wyniki. Warto również pamiętać, że na dystrybutorach w USA ilość tankowanego paliwa pokazywana jest w dziesiątych częściach galona (np. 10.5 gallons), a nie w setnych jak w przypadku litrów w Polsce. Używanie naszego kalkulatora eliminuje te wszystkie potencjalne błędy, zapewniając precyzyjne przeliczenia.',
  },
  {
    q: 'Czy ceny paliwa w USA są niższe niż w Polsce?',
    a: 'Porównanie cen paliwa między USA a Polską często prowadzi do błędnych wniosków z powodu różnic w jednostkach miary i walutach. Na pierwszy rzut oka cena 3,50 USD za galon może wydawać się niska, ale po przeliczeniu daje to około 3,70 PLN za litr (przy kursie 4 PLN za 1 USD), co jest porównywalne z cenami w Polsce. Rzeczywista różnica cenowa zależy od aktualnego kursu dolara oraz regionu USA - ceny paliwa mogą się znacząco różnić między stanami z powodu różnych podatków stanowych. Kalifornia słynie z wysokich cen paliwa, podczas gdy w Teksasie czy stanach południowych ceny są zwykle niższe. Dodatkową kwestią jest rodzaj paliwa - w USA standardem jest benzyna 87 oktanów (Regular), która jest odpowiednikiem polskiej 95, a droższa Premium (91-93 oktany) odpowiada naszej 98. Nasz kalkulator uwzględnia te wszystkie niuanse, dzięki czemu możesz realnie porównać koszty tankowania w obu krajach.',
  },
  {
    q: 'Jak zaplanować budżet na paliwo podczas roadtripu po USA?',
    a: 'Planowanie budżetu na paliwo podczas roadtripu po Stanach wymaga uwzględnienia kilku kluczowych czynników. Najpierw określ trasę i całkowitą liczbę mil do przejechania - możesz użyć Google Maps, który podaje dystans w milach. Następnie ustal średnie spalanie wynajmowanego samochodu w MPG. Podziel całkowity dystans przez wartość MPG, aby obliczyć potrzebną liczbę galonów. Przykładowo, dla trasy 2000 mil i samochodu o spalaniu 25 MPG, będziesz potrzebować 80 galonów paliwa (2000 ÷ 25 = 80). Sprawdź średnie ceny paliwa w stanach, przez które będziesz przejeżdżać - strona GasBuddy.com jest świetnym źródłem tych informacji. Pomnóż liczbę galonów przez średnią cenę w USD, a następnie przelicz na złotówki. Pamiętaj, aby dodać 10-15% zapasu na nieprzewidziane objazdy, korki lub trudne warunki drogowe. Nasz kalkulator automatyzuje wszystkie te obliczenia, umożliwiając precyzyjne zaplanowanie budżetu na paliwo.',
  },
];

/* ─── Component ────────────────────────────────────────────────────────── */
const FuelPortal = () => {
  /* state — logic unchanged */
  const [darkMode, setDarkMode]               = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]   = useState(false);
  const [pricesOpen, setPricesOpen]           = useState(false);
  const [mobilePricesOpen, setMobilePricesOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [dollarRate, setDollarRate]           = useState(4.12);
  const [fuelPrice]                           = useState(3.50);
  const [fuelPricePLN, setFuelPricePLN]       = useState(6.50);
  const [amount, setAmount]                   = useState('');
  const [conversionDirection, setConversionDirection] = useState('toZloty');
  const [calculatedResult, setCalculatedResult] = useState(null);
  const [isLoading, setIsLoading]             = useState(false);
  const [activeTab, setActiveTab]             = useState('consumption');
  const [swapRotation, setSwapRotation]       = useState(0);

  const [mpg, setMpg]                         = useState('30');
  const [distance, setDistance]               = useState('200');
  const [distanceUnit, setDistanceUnit]       = useState('km');
  const [consumptionResult, setConsumptionResult] = useState(null);

  const [openFaq, setOpenFaq]                 = useState(null);

  /* fetch exchange rate */
  useEffect(() => {
    const fetchExchangeRate = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://api.nbp.pl/api/exchangerates/rates/a/usd/');
        if (!response.ok) throw new Error('rate fetch failed');
        const data = await response.json();
        const rate = data.rates[0].mid;
        setDollarRate(rate);
        setFuelPricePLN(fuelPrice * rate / 3.785);
      } catch {
        setDollarRate(4.12);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExchangeRate();
  }, [fuelPrice]);

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPricesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* calculator */
  const calculatePrice = useCallback((value) => {
    const n = parseFloat(value);
    if (isNaN(n) || n <= 0) { setCalculatedResult(null); return; }

    if (conversionDirection === 'toZloty') {
      const totalUSD   = n * fuelPrice;
      const totalPLN   = totalUSD * dollarRate;
      const liters     = n * 3.785;
      setCalculatedResult({
        unitPrice: (totalPLN / liters).toFixed(2),
        total: totalPLN.toFixed(2),
        convertedAmount: liters.toFixed(2),
        unit: 'litrów', currency: 'PLN',
      });
    } else {
      const gallons    = n / 3.785;
      const totalPLN   = n * fuelPricePLN;
      const totalUSD   = totalPLN / dollarRate;
      setCalculatedResult({
        unitPrice: (totalUSD / gallons).toFixed(2),
        total: totalUSD.toFixed(2),
        convertedAmount: gallons.toFixed(2),
        unit: 'galonów', currency: 'USD',
      });
    }
  }, [conversionDirection, fuelPrice, dollarRate, fuelPricePLN]);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    calculatePrice(e.target.value);
  };

  const handleSwap = () => {
    setSwapRotation(r => r + 180);
    setConversionDirection(d => d === 'toZloty' ? 'toDollar' : 'toZloty');
    setAmount('');
    setCalculatedResult(null);
  };

  /* MPG calculator */
  const handleDistanceUnitChange = (unit) => {
    if (unit === distanceUnit) return;
    const val = parseFloat(distance);
    if (!isNaN(val) && val > 0) {
      setDistance(unit === 'km'
        ? (val * 1.60934).toFixed(0)
        : (val / 1.60934).toFixed(0)
      );
    }
    setDistanceUnit(unit);
  };

  const calculateConsumption = useCallback(() => {
    const mpgV = parseFloat(mpg);
    const distV = parseFloat(distance);
    if (!mpgV || !distV || mpgV <= 0 || distV <= 0) { setConsumptionResult(null); return; }

    const distMiles = distanceUnit === 'km' ? distV / 1.60934 : distV;
    const distKm    = distanceUnit === 'km' ? distV : distV * 1.60934;

    const litersPer100km  = (235.21 / mpgV).toFixed(2);
    const gallonsUsed     = distMiles / mpgV;
    const litersUsed      = (gallonsUsed * 3.785).toFixed(2);
    const costUSD         = (gallonsUsed * fuelPrice).toFixed(2);
    const costPLN         = (parseFloat(costUSD) * dollarRate).toFixed(2);

    setConsumptionResult({
      mpg: mpgV, litersPer100km,
      gallonsUsed: gallonsUsed.toFixed(2), litersUsed,
      distanceMiles: distMiles.toFixed(1),
      distanceKm: distKm.toFixed(1),
      costUSD, costPLN,
    });
  }, [mpg, distance, distanceUnit, fuelPrice, dollarRate]);

  useEffect(() => {
    if (mpg && distance && parseFloat(mpg) > 0 && parseFloat(distance) > 0) {
      calculateConsumption();
    } else {
      setConsumptionResult(null);
    }
  }, [mpg, distance, distanceUnit, calculateConsumption]);

  /* ─── design tokens ──────────────────────────────────────────────────── */
  const dm = darkMode;

  const bg          = dm ? 'bg-[#09090B]'   : 'bg-white';
  const bgAlt       = dm ? 'bg-[#111118]'   : 'bg-[#F9FAFB]';
  const cardBg      = dm ? 'bg-[#18181B]'   : 'bg-white';
  const border      = dm ? 'border-[#27272A]' : 'border-[#5B5B5B]';
  const textPrimary = dm ? 'text-[#F9FAFB]' : 'text-[#111827]';
  const textSecond  = dm ? 'text-[#71717A]' : 'text-[#2E3137]';
  const navBg       = dm ? 'bg-[#09090B]/95 border-[#27272A]' : 'bg-white/95 border-[#5B5B5B]';
  const inputCls    = `w-full h-14 px-4 rounded-xl border text-base outline-none transition-all duration-200 ${
    dm
      ? 'bg-[#111118] border-[#27272A] text-white placeholder-[#52525B] focus:border-accent'
      : 'bg-white border-[#5B5B5B] text-[#111827] placeholder-[#9CA3AF] focus:border-accent'
  } focus:ring-2 focus:ring-accent/20`;
  const labelCls  = `block text-sm font-medium mb-1.5 ${textSecond}`;

  return (
    <div className={`min-h-screen ${bg} font-sans transition-colors duration-300`}>

      {/* ─── NAV ─────────────────────────────────────────────────────── */}
      <header className={`${navBg} border-b fixed top-0 w-full z-20 backdrop-blur-md`}>
        <div className="max-w-content mx-auto px-6 h-14 flex justify-between items-center">

          <Link to="/" className="font-bold text-lg tracking-tight text-accent">
            Galony&amp;Litry
          </Link>

          {/* desktop nav */}
          <nav className="hidden md:flex items-center gap-7">
            {[
              { to: '/przelicznik-galony-na-litry', label: 'Galony → Litry' },
              { to: '/przelicznik-litry-na-galony', label: 'Litry → Galony' },
            ].map(({ to, label }) => (
              <Link key={to} to={to}
                className={`text-sm font-medium ${textSecond} hover:${dm ? 'text-white' : 'text-[#111827]'} transition-colors underline-offset-4 hover:underline`}>
                {label}
              </Link>
            ))}

            {/* prices dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setPricesOpen(o => !o)}
                className={`flex items-center gap-1 text-sm font-medium ${textSecond} hover:${dm ? 'text-white' : 'text-[#111827]'} transition-colors`}
              >
                Ceny paliw
                <ChevronDown size={13} className={`transition-transform duration-200 ${pricesOpen ? 'rotate-180' : ''}`} />
              </button>
              {pricesOpen && (
                <div className={`absolute top-full left-0 mt-2 w-52 rounded-2xl border shadow-lg py-1.5 z-50 ${cardBg} ${border} animate-fade-in`}>
                  {cityDropdownLinks.map(({ label, path }) => (
                    <Link key={path} to={path}
                      onClick={() => setPricesOpen(false)}
                      className={`block px-4 py-2 text-sm ${textSecond} hover:text-accent transition-colors`}>
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/blog"
              className={`text-sm font-medium ${textSecond} hover:${dm ? 'text-white' : 'text-[#111827]'} transition-colors underline-offset-4 hover:underline`}>
              Blog
            </Link>
          </nav>

          {/* right controls */}
          <div className="flex items-center gap-2">
            <button onClick={() => setMobileMenuOpen(o => !o)}
              className={`md:hidden p-1.5 rounded-lg ${textSecond} hover:text-accent transition-colors`}>
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <Link to="/en"
              className={`text-sm font-medium px-2.5 py-1 rounded-lg border ${border} ${textSecond} hover:text-accent hover:border-accent transition-colors`}>
              EN
            </Link>
            <button onClick={() => setDarkMode(d => !d)}
              className={`p-1.5 rounded-lg ${textSecond} hover:text-accent transition-colors`}>
              {dm ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        {/* mobile menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${border} ${dm ? 'bg-[#09090B]' : 'bg-white'}`}>
            <div className="max-w-content mx-auto px-6 flex flex-col py-2">
              {[
                { to: '/przelicznik-galony-na-litry', label: 'Galony → Litry' },
                { to: '/przelicznik-litry-na-galony', label: 'Litry → Galony' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)}
                  className={`text-sm font-medium py-3 border-b ${border} ${textSecond} text-center`}>
                  {label}
                </Link>
              ))}
              <button onClick={() => setMobilePricesOpen(o => !o)}
                className={`text-sm font-medium py-3 border-b ${border} ${textSecond} flex items-center justify-center gap-1.5`}>
                Ceny paliw
                <ChevronDown size={13} className={`transition-transform ${mobilePricesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobilePricesOpen && cityDropdownLinks.map(({ label, path }) => (
                <Link key={path} to={path}
                  onClick={() => { setMobileMenuOpen(false); setMobilePricesOpen(false); }}
                  className={`text-sm py-2.5 border-b ${border} ${textSecond} text-center`}>
                  {label}
                </Link>
              ))}
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium py-3 ${textSecond} text-center`}>
                Blog
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <main>
        <section id="kalkulator" className="pt-28 pb-16 px-4">
          <div className="max-w-content mx-auto">

            {/* headline */}
            <div className="text-center mb-10 max-w-2xl mx-auto">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-5 ${dm ? 'bg-accent/15 text-accent-dark' : 'bg-accent-light text-accent'}`}>
                <Fuel size={11} /> Aktualny kurs NBP
              </span>
              <h1 className={`text-4xl md:text-[3.25rem] font-bold leading-tight tracking-tight mb-4 ${textPrimary}`}>
                Ile kosztuje benzyna<br className="hidden md:block" /> w USA?
              </h1>
              <p className={`text-base md:text-lg ${textSecond} leading-relaxed`}>
                Przelicz ceny paliwa z dolarów na złotówki. Kalkulator uwzględnia aktualny kurs dolara i przelicza galony na litry.
              </p>
            </div>

            {/* rate strip */}
            <div className={`max-w-2xl mx-auto flex items-center justify-center gap-6 mb-6 text-sm ${textSecond}`}>
              <span className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${isLoading ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
                {isLoading ? 'Pobieranie kursu…' : `Kurs USD: ${dollarRate.toFixed(4)} PLN`}
              </span>
              <span className={`w-px h-4 ${dm ? 'bg-[#27272A]' : 'bg-[#E5E7EB]'}`} />
              <span>Benzyna USA: ${fuelPrice.toFixed(2)}/gal</span>
            </div>

            {/* ─── CALCULATOR CARD ─────────────────────────────────────── */}
            <div className={`max-w-2xl mx-auto rounded-2xl border ${border} ${cardBg}`}>

              {/* tab switcher */}
              <div className={`p-4 border-b ${border} flex gap-2`}>
                {[
                  { id: 'consumption', icon: <Calculator size={14} />,     label: 'Kalkulator MPG' },
                  { id: 'converter',   icon: <ArrowRightLeft size={14} />, label: 'Konwerter cen' },
                ].map(({ id, icon, label }) => (
                  <button key={id} onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border-2 border-[#22e321] ${
                      activeTab === id
                        ? 'bg-accent text-white shadow-sm'
                        : `${textSecond} hover:text-accent ${dm ? 'hover:bg-white/5' : 'hover:bg-[#F3F4F6]'}`
                    }`}>
                    {icon}{label}
                  </button>
                ))}
              </div>

              <div className="p-6">

                {/* ── CONVERTER TAB ───────────────────────────────────── */}
                {activeTab === 'converter' && (
                  <div>
                    {/* direction labels */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`flex-1 text-center py-2.5 rounded-xl text-sm font-medium border ${border} ${dm ? 'text-[#A1A1AA]' : 'text-[#6B7280]'}`}>
                        {conversionDirection === 'toZloty' ? 'USD / Galony' : 'PLN / Litry'}
                      </div>

                      {/* swap */}
                      <button
                        onClick={handleSwap}
                        style={{ transform: `rotate(${swapRotation}deg)`, transition: 'transform 0.4s cubic-bezier(.4,0,.2,1)' }}
                        className="w-10 h-10 flex-shrink-0 rounded-xl bg-accent text-white flex items-center justify-center shadow-sm hover:bg-accent-hover active:scale-95 transition-colors"
                        aria-label="Zamień kierunek">
                        <ArrowRightLeft size={16} />
                      </button>

                      <div className={`flex-1 text-center py-2.5 rounded-xl text-sm font-medium border ${border} ${dm ? 'text-[#A1A1AA]' : 'text-[#6B7280]'}`}>
                        {conversionDirection === 'toZloty' ? 'PLN / Litry' : 'USD / Galony'}
                      </div>
                    </div>

                    {/* input */}
                    <div className="mb-5">
                      <label className={labelCls}>
                        {conversionDirection === 'toZloty' ? 'Liczba galonów' : 'Liczba litrów'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={conversionDirection === 'toZloty' ? 'np. 10' : 'np. 50'}
                        value={amount}
                        onChange={handleAmountChange}
                        className={`${inputCls} input-green`}
                      />
                    </div>

                    {/* result */}
                    {calculatedResult ? (
                      <div key={calculatedResult.unitPrice} className={`rounded-xl p-5 border animate-fade-in-up ${dm ? 'bg-accent/10 border-accent/30' : 'bg-accent-light border-accent/20'}`}>
                        <div className={`text-2xl font-bold mb-1 text-accent`}>
                          {conversionDirection === 'toZloty'
                            ? `${calculatedResult.unitPrice} zł / litr`
                            : `$${calculatedResult.unitPrice} / galon`}
                        </div>
                        <p className={`text-sm ${textSecond}`}>
                          Łącznie: <strong className={textPrimary}>{calculatedResult.total} {calculatedResult.currency === 'PLN' ? 'zł' : '$'}</strong>
                          {' '}za <strong className={textPrimary}>{calculatedResult.convertedAmount} {calculatedResult.unit}</strong>
                        </p>
                      </div>
                    ) : (
                      <div className={`rounded-xl p-5 border border-dashed ${border} text-center ${textSecond} text-sm`}>
                        Wpisz ilość, żeby zobaczyć wynik
                      </div>
                    )}
                  </div>
                )}

                {/* ── MPG TAB ─────────────────────────────────────────── */}
                {activeTab === 'consumption' && (
                  <div>
                    <p className={`text-sm ${textSecond} mb-5`}>
                      Przelicz spalanie z MPG na l/100km i oblicz koszt trasy w USA.
                    </p>
                    <div className="grid grid-cols-2 gap-4 mb-5">
                      <div>
                        <label className={labelCls}>Spalanie (MPG)</label>
                        <input type="number" min="0" placeholder="np. 25"
                          value={mpg} onChange={e => setMpg(e.target.value)}
                          className={`${inputCls} input-green`} />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`text-sm font-medium ${textSecond}`}>
                            Dystans ({distanceUnit})
                          </span>
                          <div className={`flex rounded-lg border ${border} overflow-hidden text-xs font-medium`}>
                            {['km', 'mile'].map(unit => (
                              <button key={unit}
                                onClick={() => handleDistanceUnitChange(unit)}
                                className={`px-2.5 py-1 transition-colors ${
                                  distanceUnit === unit
                                    ? 'bg-accent text-white'
                                    : `${textSecond} ${dm ? 'hover:bg-white/10' : 'hover:bg-[#F3F4F6]'}`
                                }`}>
                                {unit}
                              </button>
                            ))}
                          </div>
                        </div>
                        <input type="number" min="0"
                          placeholder={distanceUnit === 'km' ? 'np. 200' : 'np. 125'}
                          value={distance} onChange={e => setDistance(e.target.value)}
                          className={`${inputCls} input-green`} />
                      </div>
                    </div>

                    {consumptionResult && (
                      <div key={consumptionResult.costPLN} className="animate-fade-in-up space-y-3">
                        {/* hero result */}
                        <div className={`rounded-xl p-5 border ${dm ? 'bg-accent/10 border-accent/30' : 'bg-accent-light border-accent/20'}`}>
                          <div className="text-2xl font-bold text-accent mb-0.5">
                            {consumptionResult.costPLN} zł
                          </div>
                          <p className={`text-sm ${textSecond}`}>
                            Koszt paliwa na {consumptionResult.distanceKm} km ({consumptionResult.distanceMiles} mil)
                          </p>
                        </div>

                        {/* details grid */}
                        <div className={`grid grid-cols-2 gap-3 text-sm`}>
                          {[
                            { label: 'Spalanie', value: `${consumptionResult.litersPer100km} l/100km` },
                            { label: 'Zużycie paliwa', value: `${consumptionResult.litersUsed} l (${consumptionResult.gallonsUsed} gal)` },
                            { label: 'Koszt w USD', value: `$${consumptionResult.costUSD}` },
                            { label: 'Koszt w PLN', value: `${consumptionResult.costPLN} zł` },
                          ].map(({ label, value }) => (
                            <div key={label} className={`rounded-xl p-3.5 border ${border} ${cardBg}`}>
                              <div className={`text-xs ${textSecond} mb-0.5`}>{label}</div>
                              <div className={`font-semibold ${textPrimary}`}>{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
            {/* end calculator card */}

          </div>
        </section>

        {/* ─── FUEL PRICE TILES ──────────────────────────────────────── */}
        <section className={`${bgAlt} py-14 px-4`}>
          <div className="max-w-content mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${textPrimary}`}>
                Aktualne ceny paliw w USA
              </h2>
              <Link to="/aktualne-ceny-paliw-w-usa"
                className={`text-sm font-medium text-accent flex items-center gap-1 hover:gap-2 transition-all`}>
                Wszystkie stany <ChevronRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {cityDropdownLinks.map(({ label, path }) => (
                <Link key={path} to={path}
                  className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border ${border} ${cardBg} hover:border-accent group transition-all duration-200`}>
                  <MapPin size={13} className={`${textSecond} group-hover:text-accent transition-colors flex-shrink-0`} />
                  <span className={`text-sm font-medium ${textPrimary} group-hover:text-accent transition-colors`}>{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ─── TOOLS / FEATURES ──────────────────────────────────────── */}
        <section className="py-16 px-4">
          <div className="max-w-content mx-auto">
            <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>Kalkulatory i przeliczniki amerykańskich jednostek MPG na Litry</h2>

            {/* bento grid — asymmetric */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* wide card */}
              <Link to="/przelicznik-galony-na-litry"
                className={`md:col-span-2 group flex flex-col justify-between p-6 rounded-2xl border ${border} ${cardBg} hover:border-accent transition-all duration-200 min-h-[140px]`}>
                <div>
                  <ArrowRightLeft size={20} className="text-accent mb-3" />
                  <h3 className={`font-semibold text-base mb-1 ${textPrimary}`}>Przelicznik galony → litry</h3>
                  <p className={`text-sm ${textSecond}`}>Sprawdź ile litrów to 1 galon i oblicz koszt paliwa w PLN</p>
                </div>
                <span className="text-accent text-sm font-medium mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Przelicz <ChevronRight size={14} />
                </span>
              </Link>

              {/* narrow card */}
              <Link to="/przelicznik-litry-na-galony"
                className={`group flex flex-col justify-between p-6 rounded-2xl border ${border} ${cardBg} hover:border-accent transition-all duration-200`}>
                <div>
                  <ArrowRightLeft size={20} className="text-accent mb-3 rotate-90" />
                  <h3 className={`font-semibold text-base mb-1 ${textPrimary}`}>Litry → Galony</h3>
                  <p className={`text-sm ${textSecond}`}>Przelicz litry na galony US</p>
                </div>
                <span className="text-accent text-sm font-medium mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Przelicz <ChevronRight size={14} />
                </span>
              </Link>

              {/* MPG card */}
              <Link to="/#kalkulator"
                className={`group flex flex-col justify-between p-6 rounded-2xl border ${border} ${cardBg} hover:border-accent transition-all duration-200`}>
                <div>
                  <Calculator size={20} className="text-accent mb-3" />
                  <h3 className={`font-semibold text-base mb-1 ${textPrimary}`}>Kalkulator MPG</h3>
                  <p className={`text-sm ${textSecond}`}>Przelicz spalanie na l/100km, oblicz koszt trasy</p>
                </div>
                <span className="text-accent text-sm font-medium mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Oblicz <ChevronRight size={14} />
                </span>
              </Link>

              {/* blog card — wider */}
              <Link to="/blog"
                className={`md:col-span-2 group flex items-center justify-between p-6 rounded-2xl border ${border} ${dm ? 'bg-accent/10 border-accent/30' : 'bg-accent-light border-accent/20'} hover:border-accent transition-all duration-200`}>
                <div>
                  <h3 className={`font-semibold text-base mb-1 text-accent`}>Blog — porady o podróżach po USA</h3>
                  <p className={`text-sm ${textSecond}`}>Jak tankować, ceny paliwa w stanach, import aut z USA i więcej</p>
                </div>
                <ChevronRight size={18} className="text-accent flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* ─── JAK PRZELICZYĆ GALONY NA LITRY ────────────────────────── */}
        <section className="py-14 px-4">
          <div className="max-w-content mx-auto max-w-3xl">
            <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>
              Jak przeliczyć galony na litry?
            </h2>
            <p className={`text-sm leading-relaxed ${textSecond}`}>
              Przeliczanie galonów amerykańskich na litry to podstawowa umiejętność podczas podróży po USA.
              Jeden galon amerykański to dokładnie 3,78541 litra. Dla łatwiejszych obliczeń "w głowie" możesz
              używać przybliżonej wartości 3,8 litra. Czyli kiedy widzisz na stacji benzynowej zbiornik o pojemności
              15 galonów, oznacza to około 57 litrów (15 × 3,8 = 57). Warto również pamiętać, że istnieje różnica
              między galonem amerykańskim (US gallon) a galonem imperialnym (UK gallon), który jest używany
              w Wielkiej Brytanii. Galon imperialny to około 4,55 litra, więc jest większy od amerykańskiego.
              W USA zawsze używany jest galon amerykański, więc nie musisz się martwić tą różnicą podczas podróży
              po Stanach. Nasz kalkulator zawsze używa poprawnego przelicznika dla galona amerykańskiego,
              zapewniając dokładne wyniki.
            </p>
          </div>
        </section>

        {/* ─── FAQ ───────────────────────────────────────────────────── */}
        <section className={`${bgAlt} py-16 px-4`}>
          <div className="max-w-content mx-auto">
            <h2 className={`text-xl font-bold mb-8 ${textPrimary}`}>
              Najczęściej zadawane pytania
            </h2>

            <div className={`rounded-2xl border ${border} overflow-hidden ${cardBg}`}>
              {FAQ_ITEMS.map((item, idx) => {
                const open = openFaq === idx;
                return (
                  <div key={idx} className={`border-b last:border-b-0 ${border}`}>
                    <button
                      onClick={() => setOpenFaq(open ? null : idx)}
                      className={`w-full flex items-center justify-between px-4 py-4 text-left transition-colors ${dm ? 'hover:bg-white/5' : 'hover:bg-[#F9FAFB]'}`}
                    >
                      <span className={`text-sm font-semibold pr-4 ${textPrimary}`}>{item.q}</span>
                      <ChevronDown size={16} className={`flex-shrink-0 text-accent transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && (
                      <div className={`px-4 pb-5 text-sm leading-relaxed text-left ${textSecond} animate-fade-in-up`}>
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

      </main>

      {/* ─── FOOTER ────────────────────────────────────────────────── */}
      <footer className={`${dm ? 'bg-[#18181B] border-[#27272A]' : 'bg-[#374151] border-[#4B5563]'} border-t py-8 px-4`}>
        <div className="max-w-content mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-bold text-sm text-accent">Galony&amp;Litry.pl</span>
          <p className="text-xs text-[#D1D5DB]">
            © 2026 Galony&amp;Litry.pl · Wszystkie przeliczniki mają charakter informacyjny
          </p>
          <div className="flex flex-col md:flex-row items-center gap-3 text-xs text-[#9CA3AF]">
            <Link to="/en" className="hover:text-accent transition-colors">
              English version →
            </Link>
            <span className="hidden md:inline text-[#6B7280]">·</span>
            <span>
              Projekt i kodowanie:{' '}
              <a href="https://crowbox.pl" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Crowbox.pl</a>
              {' '}&amp;{' '}
              <a href="https://reklamaprawnika.pl" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Reklamaprawnika.pl</a>
            </span>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default FuelPortal;
